import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES } from '@bolao/shared';
import { useSession } from './auth-client.js';
import { useActor } from './lib/useActor.js';
import CookieConsent from './components/CookieConsent.js';
import AuthScreen from './screens/AuthScreen.js';
import GroupsScreen from './screens/GroupsScreen.js';
import GroupScreen from './screens/GroupScreen.js';
import PoolScreen from './screens/PoolScreen.js';
import BolaoSettingsScreen from './screens/BolaoSettingsScreen.js';
import CreateCircleScreen from './screens/CreateCircleScreen.js';
import CreateGroupScreen from './screens/CreateGroupScreen.js';
import CreateCustomCompetitionScreen from './screens/CreateCustomCompetitionScreen.js';
import AddQuestionScreen from './screens/AddQuestionScreen.js';
import EditQuestionResultsScreen from './screens/EditQuestionResultsScreen.js';
import SelectSportScreen from './screens/SelectSportScreen.js';
import JoinScreen from './screens/JoinScreen.js';
import GuestGateScreen from './screens/GuestGateScreen.js';
import UpgradeScreen from './screens/UpgradeScreen.js';
import PredictionsScreen from './screens/PredictionsScreen.js';
import LeaderboardScreen from './screens/LeaderboardScreen.js';
import ProfileScreen from './screens/ProfileScreen.js';
import ChartScreen from './screens/ChartScreen.js';
import RulesScreen from './screens/RulesScreen.js';
import SettingsScreen from './screens/SettingsScreen.js';
import FaceLandmarksExperimentScreen from './screens/FaceLandmarksExperimentScreen.js';
import PrivacyScreen from './screens/PrivacyScreen.js';
import TermsScreen from './screens/TermsScreen.js';

function Loading() {
  const { t } = useTranslation();
  return (
    <main className="screen">
      <p className="muted">{t('auth.checkingSession')}</p>
    </main>
  );
}

/**
 * Everything behind sign-in.
 *
 * Two kinds of person get through: account holders, and guests playing a
 * single circle by link and PIN (PLAN.md Phase 10.29). `accountOnly` marks the
 * routes the second kind must not reach — creating circles and bolões, editing
 * questions, account settings — and sends a guest back to the one circle they
 * belong to rather than to a sign-in screen they have no use for. The API
 * refuses the same things independently (`auth/context.ts`'s `accountOf`); this
 * is navigation, not the control.
 *
 * It also applies the account's saved language preference, if there is one — a
 * choice made in Settings on another device should follow the person here,
 * not just live in this browser's `localStorage` (PLAN.md Phase 7). Guests have
 * no such preference to apply; their language stays whatever this browser says.
 */
function RequireActor({
  children,
  accountOnly = false,
}: {
  children: React.ReactNode;
  accountOnly?: boolean;
}) {
  const { actor, isPending, isGuest } = useActor();
  const { data: session } = useSession();
  const { i18n } = useTranslation();
  // Better Auth's client type doesn't know about our `locale` additional
  // field without wiring its type-inference plugin across the workspace
  // boundary — a cast here is simpler than that for one nullable string.
  const savedLocale = (session?.user as { locale?: string | null } | undefined)?.locale ?? null;

  useEffect(() => {
    if (!savedLocale) return;
    if (!SUPPORTED_LOCALES.includes(savedLocale as (typeof SUPPORTED_LOCALES)[number])) return;
    if (i18n.resolvedLanguage !== savedLocale) void i18n.changeLanguage(savedLocale);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the session's own locale changes.
  }, [savedLocale]);

  if (isPending) return <Loading />;
  if (!actor) return <Navigate to="/" replace />;
  if (accountOnly && isGuest) return <Navigate to={`/groups/${actor.guestGroupId}`} replace />;
  return <>{children}</>;
}

/** The account-only guard, kept as its own name because most routes are one. */
function RequireAccount({ children }: { children: React.ReactNode }) {
  return <RequireActor accountOnly>{children}</RequireActor>;
}

/**
 * The root URL.
 *
 * A guest has no "your groups" to show — they belong to exactly one circle and
 * cannot make or join another — so the honest landing page for them is that
 * circle itself, not a list of one.
 */
function Home() {
  const { actor, isPending, isGuest } = useActor();
  if (isPending) return <Loading />;
  if (isGuest && actor) return <Navigate to={`/groups/${actor.guestGroupId}`} replace />;
  return actor ? <GroupsScreen /> : <AuthScreen />;
}

export default function App() {
  return (
    <BrowserRouter>
      <CookieConsent />
      <Routes>
        <Route path="/" element={<Home />} />
        {/* Just a name (PLAN.md Phase 10.18) — the sport/competition picker
            below is reached afterward, from the circle's own page, never as
            part of creating the circle itself. */}
        <Route
          path="/groups/new"
          element={
            <RequireAccount>
              <CreateCircleScreen />
            </RequireAccount>
          }
        />
        <Route
          path="/groups/:id"
          element={
            <RequireActor>
              <GroupScreen />
            </RequireActor>
          }
        />
        {/* Adding a bolão to an existing circle. Football and Custom are the
            only sports wired up today (PLAN.md Phase 10.3/10.4) — the sport
            picker is the only thing that links to either. */}
        <Route
          path="/groups/:id/bolaos/new"
          element={
            <RequireAccount>
              <SelectSportScreen />
            </RequireAccount>
          }
        />
        <Route
          path="/groups/:id/bolaos/new/football"
          element={
            <RequireAccount>
              <CreateGroupScreen />
            </RequireAccount>
          }
        />
        <Route
          path="/groups/:id/bolaos/new/custom"
          element={
            <RequireAccount>
              <CreateCustomCompetitionScreen />
            </RequireAccount>
          }
        />
        <Route
          path="/groups/:id/bolaos/new/baby-shower"
          element={
            <RequireAccount>
              <CreateCustomCompetitionScreen preset="baby-shower" />
            </RequireAccount>
          }
        />
        <Route
          path="/groups/:id/bolaos/:poolId"
          element={
            <RequireActor>
              <PoolScreen />
            </RequireActor>
          }
        />
        <Route
          path="/groups/:id/bolaos/:poolId/predictions"
          element={
            <RequireActor>
              <PredictionsScreen />
            </RequireActor>
          }
        />
        {/* Organizer-only (PLAN.md 10.28) — the gear icon PoolNav shows next
            to the title is the only thing that links here; the service
            layer itself also rejects a non-organizer. */}
        <Route
          path="/groups/:id/bolaos/:poolId/settings"
          element={
            <RequireAccount>
              <BolaoSettingsScreen />
            </RequireAccount>
          }
        />
        {/* Custom competitions only (PLAN.md Phase 10.3/10.4) — the "+" card
            at the end of the question list is the only thing that links here;
            the service layer itself also rejects a non-organizer. */}
        <Route
          path="/groups/:id/bolaos/:poolId/questions/new"
          element={
            <RequireAccount>
              <AddQuestionScreen />
            </RequireAccount>
          }
        />
        {/* Organizer-only, two separate entry points (PLAN.md 10.20): "Enter
            results" is exclusively the real-world outcome; "Edit question"
            is everything else (deadline, lock, display/scoring settings). */}
        <Route
          path="/groups/:id/bolaos/:poolId/questions/:questionId/edit"
          element={
            <RequireAccount>
              <EditQuestionResultsScreen mode="rules" />
            </RequireAccount>
          }
        />
        <Route
          path="/groups/:id/bolaos/:poolId/questions/:questionId/results"
          element={
            <RequireAccount>
              <EditQuestionResultsScreen mode="results" />
            </RequireAccount>
          }
        />
        <Route
          path="/groups/:id/bolaos/:poolId/leaderboard"
          element={
            <RequireActor>
              <LeaderboardScreen />
            </RequireActor>
          }
        />
        <Route
          path="/groups/:id/bolaos/:poolId/participants/:participantId"
          element={
            <RequireActor>
              <ProfileScreen />
            </RequireActor>
          }
        />
        <Route
          path="/groups/:id/bolaos/:poolId/chart"
          element={
            <RequireActor>
              <ChartScreen />
            </RequireActor>
          }
        />
        <Route
          path="/groups/:id/bolaos/:poolId/rules"
          element={
            <RequireActor>
              <RulesScreen />
            </RequireActor>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAccount>
              <SettingsScreen />
            </RequireAccount>
          }
        />
        {/* Gated a level further than RequireAccount, inside the screen itself
            (lib/testers.ts) — an internal diagnostic for one account, not a
            circle/bolão-scoped feature, so it lives at the top level like
            /settings rather than nested under /groups. */}
        <Route
          path="/experiments/face-landmarks"
          element={
            <RequireAccount>
              <FaceLandmarksExperimentScreen />
            </RequireAccount>
          }
        />
        {/*
          Deliberately unguarded. The recipient may not have an account yet, and
          must be able to see what they are being invited to before deciding to
          sign in. The screen itself requires sign-in before joining.
        */}
        <Route path="/join/:token" element={<JoinScreen />} />
        {/*
          Also unguarded, and for a stronger reason: this is the screen for
          someone who has deliberately chosen NOT to have an account. It shows
          the circle's roster so they can pick themselves off it, or add
          themselves to it (PLAN.md Phase 10.29). The invite token in the URL
          is the only thing standing in front of it, which is the same gate
          the link itself already is.
        */}
        <Route path="/join/:token/players" element={<GuestGateScreen />} />
        {/*
          Where Google returns a guest who chose to register. Unguarded because
          it runs at the exact moment the visitor holds both a guest cookie and
          a brand-new account session, and belongs to neither guard.
        */}
        <Route path="/upgrade" element={<UpgradeScreen />} />
        {/* Unguarded: readable before signing in, and linked from the cookie banner. */}
        <Route path="/privacy" element={<PrivacyScreen />} />
        <Route path="/terms" element={<TermsScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
